// src/pages/kpi/components/BaseKPIPage.jsx

import { ArrowLeft, Filter } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Charts from "../components/charts/charts";
import KPIFilterSidebar from "../components/kpi/KPIFilterSidebar";
import KPIPagination from "../components/kpi/KPIPagination";
import SummaryStats from "../components/kpi/SummaryStats";
import Navbar from "../components/navigation/Navbar";
import Button from "../components/ui/Button";
import { TableComponent } from "../components/ui/Table";
import { INITIAL_PAGINATION } from "../constants/kpiConstants";
import { useRegions } from "../context/RegionsContext";
import { settings } from "../library/allauth";
import {
  buildQueryParams,
  calculateSummaryStats,
  formatDateForBackend,
  generateTableColumns,
  getApiOptions,
  handleApiResponse,
  loadSavedFilters,
  validateFilters,
} from "../utils/kpiUtils";

/**
 * Base reusable KPI page component
 *
 * @param {Object} config - Page configuration from KPI_PAGE_CONFIG
 * @param {boolean} showInterval - Show interval input (default: true)
 * @param {boolean} showEndDate - Show end date input (default: false)
 * @param {boolean} showAggregation - Show aggregation selector (default: true)
 */
export default function BaseKPIPage({
  config,
  showInterval = true,
  showEndDate = false,
  showAggregation = true,
}) {
  const navigate = useNavigate();
  const { allSystems, isLoading: regionsLoading } = useRegions();

  // Compute dynamic defaults once per mount; stable reference for deps below.
  const defaultFilters = useMemo(() => config.defaultFilters(), [config]);

  // State Management
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState([]);
  const [filteredChartData, setFilteredChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterSidebar, setFilterSidebar] = useState(false);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [selectedRows, setSelectedRows] = useState([]);

  const activeData = filteredChartData !== null ? filteredChartData : data;

  const tableColumns = useMemo(() => generateTableColumns(activeData), [activeData]);
  const baseUrl = useMemo(() => settings.baseUrl || "", []);
  const summaryStats = useMemo(
    () => (config.showSummaryStats ? calculateSummaryStats(activeData) : null),
    [activeData, config.showSummaryStats],
  );

  const handleCSVDownload = useCallback(async () => {
    try {
      console.log("Starting CSV download from displayed data...");

      // 4. UPDATE CSV DOWNLOADER TO USE ACTIVE DATA
      if (!activeData || activeData.length === 0) {
        alert("No data available to download. Please fetch data first.");
        return;
      }

      const allData = activeData;

      console.log(`Downloading ${allData.length} records from current view`);

      // Convert to CSV
      const headers = Object.keys(allData[0]).join(",");
      const rows = allData.map((row) =>
        Object.values(row)
          .map((val) => {
            if (val === null || val === undefined) return "";
            if (
              typeof val === "string" &&
              (val.includes(",") || val.includes("\n"))
            ) {
              return `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          })
          .join(","),
      );
      const csv = [headers, ...rows].join("\n");

      // Download
      // Get site name
// TODO: is there a jsite_name or system_name (its not in the db) check usage and update
      const systemName =
        allSystems.find((s) => s.system_id === parseInt(filters.systemId))
          ?.site_name || `System ${filters.systemId}`;

      // Calculate duration in hours
      let durationInfo = "";
      if (filters.endDate && filters.startDate) {
        const start = new Date(filters.startDate + "T" + filters.startTime);
        const end = new Date(
          filters.endDate + "T" + (filters.endTime || "23:59:59"),
        );
        const diffMs = end - start;
        const diffHours = diffMs / (1000 * 60 * 60);
        // Remove .0 if whole number
        const formattedHours =
          diffHours % 1 === 0 ? diffHours.toFixed(0) : diffHours.toFixed(1);
        durationInfo = `_${formattedHours}hrs`;
      } else {
        // Single date with interval
        const intervalHours = parseInt(filters.interval || 60) / 3600;
        // Remove .0 if whole number
        const formattedHours =
          intervalHours % 1 === 0
            ? intervalHours.toFixed(0)
            : intervalHours.toFixed(1);
        durationInfo = `_${formattedHours}hrs`;
      }

      // Build filename based on page type
      const parts = [config.tableTitle.replace(/ /g, "_")];
      parts.push(systemName.replace(/ /g, "_"));

      if (filters.aggregationLevel) {
        parts.push(filters.aggregationLevel);
      }

      parts.push(filters.startDate);
      parts.push(durationInfo.replace("_", ""));

      const filename = parts.join("_") + ".csv";

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert(`Downloading ${allData.length} records. Please save the file when prompted.`);
      }, 100);
    } catch (err) {
      console.error("Error downloading CSV:", err);
      alert("Failed to download CSV: " + err.message);
    }
  }, [activeData, config.tableTitle, allSystems, filters]);
  /**
   * Fetch data with pagination
   */
  const fetchData = useCallback(
    async (page = 1) => {
      const validationErrors = validateFilters(filters);
      if (validationErrors.length > 0) {
        setError(validationErrors.join(", "));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        
        const query = buildQueryParams(filters, {
          page,
          pageSize: pagination.pageSize,
        });

        const response = await fetch(
          `${baseUrl}${config.endpoint}?${query}`,
          getApiOptions(),
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

         const responseData = await response.json();
        const { data: fetchedData, pagination: paginationData } =
          handleApiResponse(responseData, pagination.pageSize);

        setData(fetchedData);
        setFilteredChartData(null);
        setPagination({
          ...paginationData,
          pageSize: pagination.pageSize,
          currentPage: page,
        });
      } catch (err) {
        console.error(`Error fetching ${config.title}:`, err);
        setError(
          err.message || `Failed to fetch ${config.title.toLowerCase()} data`,
        );
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [filters, baseUrl, pagination.pageSize, config.endpoint, config.title],
  );

  // EVENT HANDLERS

  const handleInputChange = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      fetchData(1);
    },
    [fetchData],
  );

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    setData([]);
    setError(null);
    setPagination(INITIAL_PAGINATION);
    setSelectedRows([]);
  }, [defaultFilters]);

  const toggleFilterSidebar = useCallback(() => {
    setFilterSidebar((prev) => !prev);
  }, []);

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage >= 1 && newPage <= pagination.totalPages) {
        fetchData(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [fetchData, pagination.totalPages],
  );

  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
      fetchData(1);
    },
    [fetchData],
  );

  const handleBackToDashboard = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  // EFFECTS

  useEffect(() => {
    const savedFilters = loadSavedFilters(
      config.sessionStorageKey,
      defaultFilters,
    );
    if (savedFilters) {
      setFilters(savedFilters);
      fetchData(1);
    }
  }, [config.sessionStorageKey, defaultFilters, fetchData]);

  // Once allSites loads, resolve the default siteId into a concrete
  // { siteId, streetId } pair so the filter Select and active-filters chip
  // can find a matching site (mirrors Dashboard.js).
  useEffect(() => {
    if (!allSystems || allSystems.length === 0) return;
    if (filters.streetId != null && filters.streetId !== "") return;
    const match = allSystems.find(
      (s) => String(s.system_id) === String(filters.systemId),
    );
    if (!match) return;
    setFilters((prev) => ({
      ...prev,
      systemId: match.system_id,
      streetId: match.street_id,
    }));
  }, [allSystems, filters.systemId, filters.streetId]);

  // RENDER

  return (
    <>
      <Navbar />
      <div className="bg-gray-100 min-h-screen pt-14">
        <div className="flex flex-col px-4 sm:px-8 md:px-16 py-4 sm:py-6 md:py-8">
          {/* Breadcrumb & Header */}
          <div className="mb-4">
            <button
              onClick={handleBackToDashboard}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft size={16} />
              Back to KPI Dashboard
            </button>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-medium text-[#191d23]">
                {config.title}
              </h1>
              <p className="text-sm sm:text-base text-slate-500">
                {config.subtitle}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => fetchData(1)}
                variant="primary"
                disabled={loading}
              >
                {loading ? "Loading..." : "Fetch Data"}
              </Button>

              <Button
                onClick={toggleFilterSidebar}
                variant="outline"
                icon={<Filter size={24} strokeWidth={2.25} />}
              />
            </div>
          </div>

          {/* Summary Statistics */}
          {config.showSummaryStats && <SummaryStats stats={summaryStats} />}

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Chart Section */}
            <div className={`${filterSidebar ? "lg:flex-1" : "w-full"}`}>
              {loading && data.length === 0 ? (
                <div className="flex items-center justify-center h-64 bg-white rounded shadow">
                  <div className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <p className="text-gray-500">
                      Loading data, please be patient...
                    </p>
                  </div>
                </div>
              ) : error && data.length === 0 ? (
                <div className="flex items-center justify-center h-64 bg-white rounded shadow">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : (
                <Charts
                  data={data}
                  showControls={true}
                  title={config.chartTitle}
                  subtitle={config.chartSubtitle}
                  columnMapping={config.columnMapping}
                  availableUserTypes={config.availableUserTypes}
                  onDataFiltered={setFilteredChartData}
                />
              )}
            </div>

            {/* Desktop Filter Sidebar */}
            <KPIFilterSidebar
              isOpen={filterSidebar}
              onClose={toggleFilterSidebar}
              filters={filters}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
              onReset={handleReset}
              loading={loading}
              error={error}
              showInterval={showInterval}
              showEndDate={showEndDate}
              showAggregation={showAggregation}
              isMobile={false}
              systems={allSystems}
            />
          </div>

          {/* Pagination */}
          <KPIPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
          />

          {/* Table Section */}
          {activeData.length > 0 ? (
            <div className="mt-6">
              <TableComponent
                columns={tableColumns}
                data={activeData}
                selectable
                selectedRows={selectedRows}
                onSelectRows={setSelectedRows}
                onRowClick={(row) => console.log("Clicked row:", row)}
                sortable
                loading={loading}
                tableTitle={config.tableTitle}
                download={handleCSVDownload}
              />
            </div>
          ) : (
            // Handle the case where the user filters down to 0 items so the table doesn't break
            !loading && data.length > 0 && (
              <div className="mt-6 flex items-center justify-center p-8 bg-white border border-gray-200 rounded-lg">
                <p className="text-gray-500">No data matches the current user filter.</p>
              </div>
            )
          )}
        </div>

        {/* Mobile Filter Sidebar */}
        <KPIFilterSidebar
          isOpen={filterSidebar}
          onClose={toggleFilterSidebar}
          filters={filters}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onReset={handleReset}
          loading={loading}
          error={error}
          showInterval={showInterval}
          showEndDate={showEndDate}
          showAggregation={showAggregation}
          isMobile={true}
          systems={allSystems}
        />
      </div>
    </>
  );
}
